import { createElement, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import {
  Calendar, Search, Siren, ArrowRight, ShieldCheck, Award, Stethoscope, User, CalendarDays,
  ChevronLeft, ChevronRight, Play, Pause,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { useDoctors } from "@/hooks/useDoctors";
import type { HeroSlide as HeroSlideData } from "@/hooks/useHomeSettings";
import knmcBuilding from "@/assets/knmc-building.jpeg";
import knmcSurgery from "@/assets/knmc-surgery.jpeg";
import knmcFacilities from "@/assets/knmc-facilities.jpeg";
import knmcOpd from "@/assets/knmc-opd.jpeg";

type Slide = {
  kind: "image" | "video";
  src: string;
  poster?: string;
  alt: string;
  eyebrow: string;
  title: React.ReactNode;
  description: string;
  primaryCta?: { label: string; to?: string; icon?: React.ReactNode };
  secondaryCta?: { label: string; to?: string; icon?: React.ReactNode };
};

const DEFAULT_SLIDES: Slide[] = [
  {
    kind: "image",
    src: knmcBuilding,
    alt: "Kathmandu National Medical College & Teaching Hospital",
    eyebrow: "Tertiary Care",
    title: (
      <>
        Advanced Healthcare.{" "}
        <span className="italic text-gold">Compassionate</span> Care.
      </>
    ),
    description:
      "World-class medical care, advanced diagnostics, and expert doctors across 12+ specialities — trusted by thousands across Nepal.",
    primaryCta: { label: "Book Appointment", to: "/doctors", icon: <Calendar className="mr-2 h-4 w-4" /> },
    secondaryCta: { label: "Find a Doctor", to: "/doctors", icon: <Search className="mr-2 h-4 w-4" /> },
  },
  {
    kind: "image",
    src: knmcFacilities,
    alt: "Modern operating theatre at KNMC",
    eyebrow: "State-of-the-art Facilities",
    title: (
      <>
        Precision surgery. <span className="italic text-gold">Modern</span> theatres.
      </>
    ),
    description:
      "Twelve modular operating theatres equipped for cardiac, neuro, orthopaedic and minimally invasive procedures — staffed 24/7.",
    primaryCta: { label: "Explore Facilities", to: "/about", icon: <ArrowRight className="mr-2 h-4 w-4" /> },
    secondaryCta: { label: "Our Departments", to: "/departments", icon: <Stethoscope className="mr-2 h-4 w-4" /> },
  },
  {
    kind: "image",
    src: knmcOpd,
    alt: "KNMC consultant specialist",
    eyebrow: "Meet Our Specialists",
    title: (
      <>
        Doctors who <span className="italic text-gold">listen</span>, treat, and care.
      </>
    ),
    description:
      "50+ consultants across cardiology, neurology, paediatrics, orthopaedics and more — book directly online.",
    primaryCta: { label: "Browse Doctors", to: "/doctors", icon: <User className="mr-2 h-4 w-4" /> },
    secondaryCta: { label: "Book Appointment", to: "/doctors", icon: <Calendar className="mr-2 h-4 w-4" /> },
  },
];

const ICONS: Record<string, React.ReactNode> = {
  Calendar: <Calendar className="mr-2 h-4 w-4" />,
  Search: <Search className="mr-2 h-4 w-4" />,
  ArrowRight: <ArrowRight className="mr-2 h-4 w-4" />,
  Stethoscope: <Stethoscope className="mr-2 h-4 w-4" />,
  User: <User className="mr-2 h-4 w-4" />,
};

const AUTOPLAY_MS = 6500;

function mapSlides(heroSlides: HeroSlideData[]): Slide[] {
  return heroSlides.map((s) => ({
    kind: "image" as const,
    src: s.image || knmcBuilding,
    alt: s.title || "",
    eyebrow: s.eyebrow || "",
    title: s.title || "",
    description: s.description || "",
    primaryCta: s.primary_cta_label
      ? { label: s.primary_cta_label, to: s.primary_cta_link || undefined, icon: ICONS[s.primary_cta_icon] || ICONS[s.primary_cta_label] || <Calendar className="mr-2 h-4 w-4" /> }
      : undefined,
    secondaryCta: s.secondary_cta_label
      ? { label: s.secondary_cta_label, to: s.secondary_cta_link || undefined, icon: ICONS[s.secondary_cta_icon] || ICONS[s.secondary_cta_label] || <Search className="mr-2 h-4 w-4" /> }
      : undefined,
  }));
}

export function Hero({ slides: heroSlides, emergencyPhone }: { slides?: HeroSlideData[] | null, emergencyPhone?: string }) {
  const navigate = useNavigate();
  const { doctors } = useDoctors();
  const [index, setIndex] = useState(0);
  const [playing, setPlaying] = useState(true);
  const timerRef = useRef<number | null>(null);

  const SLIDES = useMemo(
    () => (heroSlides && heroSlides.length > 0 ? mapSlides(heroSlides) : DEFAULT_SLIDES),
    [heroSlides],
  );

  const [searchQuery, setSearchQuery] = useState("");
  const [selectedDept, setSelectedDept] = useState("");

  const deptOptions = useMemo(() => {
    const set = new Set(doctors.map((d) => d.department).filter(Boolean));
    return Array.from(set).sort();
  }, [doctors]);

  const slide = SLIDES[index];

  function handleSearch() {
    const params = new URLSearchParams();
    if (selectedDept) params.set("department", selectedDept);
    if (searchQuery.trim()) params.set("q", searchQuery.trim());
    navigate({ to: `/doctors${params.toString() ? `?${params.toString()}` : ""}` });
  }

  useEffect(() => {
    if (!playing) return;
    timerRef.current = window.setTimeout(() => {
      setIndex((i) => (i + 1) % SLIDES.length);
    }, AUTOPLAY_MS);
    return () => {
      if (timerRef.current) window.clearTimeout(timerRef.current);
    };
  }, [index, playing]);

  const goTo = (i: number) => setIndex(((i % SLIDES.length) + SLIDES.length) % SLIDES.length);
  const next = () => goTo(index + 1);
  const prev = () => goTo(index - 1);

  const badges =
    heroSlides && heroSlides[index]?.badges?.length
      ? heroSlides[index].badges
      : ["24/7 Emergency", "50+ Specialists"];

  return (
    <section className="relative overflow-hidden">
      <div className="absolute inset-0">
        {SLIDES.map((s, i) => (
          <div
            key={i}
            aria-hidden={i !== index}
            className={`absolute inset-0 transition-opacity duration-1000 ease-out ${
              i === index ? "opacity-100" : "opacity-0"
            }`}
          >
            {s.kind === "image" ? (
              <img
                src={s.src}
                alt={s.alt}
                width={1920}
                height={1280}
                className={`h-full w-full object-cover ${i === index ? "animate-ken-burns" : ""}`}
                key={`${i}-${index === i ? "active" : "idle"}`}
              />
            ) : (
              <video
                src={s.src}
                poster={s.poster}
                autoPlay
                muted
                loop
                playsInline
                className="h-full w-full object-cover"
              />
            )}
            <div className="absolute inset-0 bg-gradient-hero" />
          </div>
        ))}
      </div>

      <div className="relative mx-auto max-w-7xl px-6 pt-20 pb-28 md:pt-28 md:pb-40">
        <SlideContent key={index} slide={slide} badges={badges} emergencyPhone={emergencyPhone}/>
      </div>

      <div className="pointer-events-none absolute bottom-32 right-0 z-10 md:bottom-40">
        <div className="pointer-events-auto mr-6 flex flex-col items-end gap-3 md:mr-10">
          <div className="flex items-center gap-1 rounded-full border border-white/25 bg-black/40 p-1.5 backdrop-blur-md">
            <button
              type="button"
              onClick={prev}
              aria-label="Previous slide"
              className="inline-flex h-10 w-10 items-center justify-center rounded-full text-white transition-all hover:bg-white/15 hover:text-gold"
            >
              <ChevronLeft className="h-5 w-5" />
            </button>
            <button
              type="button"
              onClick={next}
              aria-label="Next slide"
              className="inline-flex h-10 w-10 items-center justify-center rounded-full text-white transition-all hover:bg-white/15 hover:text-gold"
            >
              <ChevronRight className="h-5 w-5" />
            </button>
            <span className="mx-1 h-5 w-px bg-white/20" />
            <button
              type="button"
              onClick={() => setPlaying((p) => !p)}
              aria-label={playing ? "Pause slideshow" : "Play slideshow"}
              className="inline-flex h-10 w-10 items-center justify-center rounded-full text-white transition-all hover:bg-white/15 hover:text-gold"
            >
              {playing ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
            </button>
          </div>

          <div className="flex items-center gap-3">
            {SLIDES.map((_, i) => {
              const active = i === index;
              return (
                <button
                  key={i}
                  type="button"
                  onClick={() => goTo(i)}
                  aria-label={`Go to slide ${i + 1}`}
                  className="group relative h-2 overflow-hidden rounded-full bg-white/25 transition-all"
                  style={{ width: active ? 56 : 22 }}
                >
                  {active && playing && (
                    <span
                      key={index}
                      className="absolute inset-0 origin-left animate-slide-progress bg-gold"
                      style={{ animationDuration: `${AUTOPLAY_MS}ms` }}
                    />
                  )}
                  {active && !playing && <span className="absolute inset-0 bg-gold" />}
                </button>
              );
            })}
            <span className="ml-2 font-display text-xs font-medium tracking-widest text-white/70">
              {String(index + 1).padStart(2, "0")}
              <span className="mx-1 text-white/30">/</span>
              {String(SLIDES.length).padStart(2, "0")}
            </span>
          </div>
        </div>
      </div>

      <div className="relative z-10 mx-auto -mt-20 max-w-6xl px-6">

        <div className="flex flex-col items-stretch gap-2 rounded-2xl border border-white/25 bg-white/10 p-2 shadow-elevated ring-1 ring-white/10 backdrop-blur-xl md:flex-row md:gap-0 md:p-3">
          <div className="group flex flex-1 items-center gap-4 rounded-xl px-6 py-4 transition-colors hover:bg-white/10 md:rounded-none md:border-r md:border-white/15">
            <div className="text-gold opacity-90 transition-opacity group-hover:opacity-100">
              <Stethoscope className="h-6 w-6" strokeWidth={1.5} />
            </div>
            <div className="flex min-w-0 flex-col">
              <span className="mb-0.5 text-[10px] font-semibold uppercase tracking-widest text-white/70">
                Department
              </span>
              <select
                value={selectedDept}
                onChange={(e) => setSelectedDept(e.target.value)}
                className="w-full cursor-pointer appearance-none border-none bg-transparent p-0 font-display text-lg font-medium text-white outline-none focus:ring-0 [&>option]:text-slate-900"
              >
                <option value="">All Departments</option>
                {deptOptions.map((dept) => (
                  <option key={dept} value={dept}>{dept}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="group flex flex-1 items-center gap-4 rounded-xl px-6 py-4 transition-colors hover:bg-white/10 md:rounded-none md:border-r md:border-white/15">
            <div className="text-gold opacity-90 transition-opacity group-hover:opacity-100">
              <User className="h-6 w-6" strokeWidth={1.5} />
            </div>
            <div className="flex flex-1 flex-col">
              <span className="mb-0.5 text-[10px] font-semibold uppercase tracking-widest text-white/70">
                Doctor
              </span>
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") handleSearch(); }}
                placeholder="Search by name"
                className="w-full border-none bg-transparent p-0 font-display text-lg font-medium text-white shadow-none outline-none placeholder:text-white/50 focus:ring-0"
              />
            </div>
          </div>

          <div className="flex items-center px-2 py-2 md:pl-4">
            <Button onClick={handleSearch} className="group/btn h-auto w-full gap-3 rounded-xl bg-gold px-8 py-4 text-base font-semibold text-gold-foreground shadow-glow hover:bg-gold/90 active:scale-95 md:w-auto">
              <Search className="h-4 w-4 transition-transform group-hover/btn:scale-110" />
              <span className="tracking-wide">Search Specialist</span>
              <ArrowRight className="h-4 w-4 transition-transform group-hover/btn:translate-x-1" />
            </Button>
          </div>
        </div>


        <div className="mt-5 flex justify-center opacity-50">
          <div className="h-px w-32 bg-gradient-to-r from-transparent via-gold to-transparent" />
        </div>
      </div>
    </section>
  );
}

function SlideContent({ slide, badges, emergencyPhone }: { slide: Slide; badges: string[]; emergencyPhone?: string }) {
  const navigate = useNavigate();

  function handleClick(to?: string) {
    if (!to) return;
    if (to.startsWith("http") || to.startsWith("tel") || to.startsWith("mailto")) {
      window.open(to, "_blank");
    } else {
      navigate({ to });
    }
  }

  return (
    <div className="max-w-3xl">
      <div
        className="mb-6 inline-flex animate-fade-up items-center gap-2 rounded-full border border-gold/40 bg-white/10 px-4 py-1.5 text-xs font-medium text-gold backdrop-blur"
        style={{ animationDelay: "100ms" }}
      >
        <Award className="h-3.5 w-3.5" /> {slide.eyebrow}
      </div>

      <h1 className="font-display text-4xl font-semibold text-white text-balance md:text-6xl lg:text-7xl">
        <AnimatedTitle node={slide.title} />
      </h1>

      <p
        className="mt-6 max-w-2xl animate-fade-up text-lg text-white/85 md:text-xl"
        style={{ animationDelay: "650ms" }}
      >
        {slide.description}
      </p>

      <div
        className="mt-10 flex flex-wrap gap-3 animate-fade-up"
        style={{ animationDelay: "850ms" }}
      >
        {slide.primaryCta && (
          <Button
            size="lg"
            className="h-12 rounded-full bg-gold px-6 text-gold-foreground hover:opacity-90"
            onClick={() => handleClick(slide.primaryCta!.to)}
          >
            {slide.primaryCta.icon}
            {slide.primaryCta.label}
          </Button>
        )}
        {slide.secondaryCta && (
          <Button
            size="lg"
            variant="outline"
            className="h-12 rounded-full border-white/30 bg-white/10 px-6 text-white backdrop-blur hover:bg-white/20"
            onClick={() => handleClick(slide.secondaryCta!.to)}
          >
            {slide.secondaryCta.icon}
            {slide.secondaryCta.label}
          </Button>
        )}
        <Button
          size="lg"
          variant="ghost"
          className="h-12 rounded-full bg-emergency px-6 text-emergency-foreground hover:bg-emergency/90"
        >
          <Siren className="mr-2 h-4 w-4" /> Emergency · {emergencyPhone}
        </Button>
      </div>

      <div
        className="mt-12 flex flex-wrap items-center gap-x-8 gap-y-3 text-sm text-white/80 animate-fade-up"
        style={{ animationDelay: "1000ms" }}
      >
        {badges.map((badge) => {
          const Icon = badge.includes("24/7") ? ShieldCheck : badge.includes("Specialist") || badge.includes("Doctors") ? Stethoscope : Award;
          return (
            <span key={badge} className="flex items-center gap-2"><Icon className="h-4 w-4 text-gold" /> {badge}</span>
          );
        })}
      </div>
    </div>
  );
}

function AnimatedTitle({ node }: { node: React.ReactNode }) {
  let wordCounter = 0;
  const stagger = 70;

  const content =
    typeof node === "string" && /<[a-z][\s\S]*>/i.test(node.trim())
      ? parseHtml(node)
      : node;

  const render = (n: React.ReactNode): React.ReactNode => {
    if (n === null || n === undefined || typeof n === "boolean") return null;
    if (typeof n === "string" || typeof n === "number") {
      const text = String(n);
      const tokens = text.split(/(\s+)/);
      return tokens.map((tok, i) => {
        if (/^\s+$/.test(tok)) return tok;
        const delay = 200 + wordCounter * stagger;
        wordCounter += 1;
        return (
          <span key={`w-${i}-${wordCounter}`} className="inline-block overflow-hidden align-bottom -m-[0.2em]">
            <span
              className="inline-block animate-word-rise p-[0.2em]"
              style={{ animationDelay: `${delay}ms` }}
            >
              {tok}
            </span>
          </span>
        );
      });
    }
    if (Array.isArray(n)) return n.map((c, i) => <span key={i}>{render(c)}</span>);
    if (typeof n === "object" && "props" in (n as object)) {
      const el = n as React.ReactElement<{ children?: React.ReactNode }>;
      const children = el.props?.children;
      return <el.type {...el.props}>{render(children)}</el.type>;
    }
    return n;
  };

  return <>{render(content)}</>;
}

function parseHtml(html: string): React.ReactNode[] {
  try {
    const processed = html
      .replace(/\{"\s*"\}/g, " ")
      .replace(/className=/g, "class=");
    const doc = new DOMParser().parseFromString(`<body>${processed}</body>`, "text/html");
    return domToFragment(doc.body.childNodes);
  } catch {
    return [html];
  }
}

function domToFragment(nodes: NodeListOf<ChildNode>): React.ReactNode[] {
  return Array.from(nodes).flatMap((node) => {
    if (node.nodeType === 3) return (node as Text).textContent || "";
    if (node.nodeType === 1) {
      const el = node as HTMLElement;
      const children = domToFragment(el.childNodes);
      const props: Record<string, string> = {};
      if (el.className) props.className = el.className;
      return createElement(el.tagName.toLowerCase(), props, ...children);
    }
    return [];
  });
}

function DateField() {
  const [type, setType] = useState<"text" | "date">("text");
  return (
    <input
      type={type}
      placeholder="Pick a date"
      onFocus={() => setType("date")}
      onBlur={(e) => {
        if (!e.currentTarget.value) setType("text");
      }}
      className="w-full border-none bg-transparent p-0 font-display text-lg font-medium text-white shadow-none outline-none placeholder:text-white/50 focus:ring-0 [color-scheme:dark]"
    />
  );
}
